import React, { useEffect, useState, useMemo, lazy, Suspense } from 'react'; // Import lazy and Suspense
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import PageErrorBoundary from './components/common/PageErrorBoundary';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { PrayerTimesProvider } from './contexts/PrayerTimesContext';
import { MosqueProvider } from './contexts/MosqueContext';
import { AthanSettingsProvider } from './contexts/AthanSettingsContext';
import { QuranSettingsProvider } from './contexts/QuranSettingsContext'; // Moved up
import ThemeToggle from './components/ThemeToggle';
import LanguageSelector from './components/LanguageSelector';
import Layout from './components/layout/Layout'; // Moved up
import apiConfig from './config/apiConfig';
import analyticsService from './services/analyticsService';

// Lazy load Pages
const HomePage = lazy(() => import('./pages/HomePage'));
const PrayerTimesPage = lazy(() => import('./pages/PrayerTimesPage'));
const CalendarPage = lazy(() => import('./pages/CalendarPage'));
const MobileAppPage = lazy(() => import('./pages/MobileAppPage'));
const ApiDocPage = lazy(() => import('./pages/ApiDocPage'));
const AlexaSkillPage = lazy(() => import('./pages/AlexaSkillPage'));
const NiyyahPage = lazy(() => import('./pages/NiyyahPage'));
const TasmiPage = lazy(() => import('./pages/TasmiPage'));
const TasmiRecordPage = lazy(() => import('./pages/TasmiRecordPage'));
const AiAgentsPage = lazy(() => import('./pages/AiAgentsPage'));
const FeatureRequestPage = lazy(() => import('./pages/FeatureRequestPage'));
const MosqueDirectoryPage = lazy(() => import('./pages/MosqueDirectoryPage'));
const MosqueDetailPage = lazy(() => import('./pages/MosqueDetailPage'));
const MasjidRequestPage = lazy(() => import('./pages/MasjidRequestPage'));
const MihrabPage = lazy(() => import('./pages/MihrabPage'));
const PrayerIntentPage = lazy(() => import('./pages/PrayerIntentPage'));
const FAQSupportPage = lazy(() => import('./pages/FAQSupportPage'));
const QiblaPage = lazy(() => import('./pages/QiblaPage'));
const QuranPage = lazy(() => import('./pages/QuranPage'));
const LifestyleCompass = lazy(() => import('./components/lifestyle-compass/LifestyleCompass'));

const LegacyPrivacyPolicyRedirect = () => {
    useEffect(() => {
        window.location.replace('/privacy-policy/');
    }, []);

    return <div>Redirecting to Privacy Policy...</div>;
};

const LegacyTermsRedirect = () => {
    useEffect(() => {
        window.location.replace('/terms/');
    }, []);

    return <div>Redirecting to Terms of Use...</div>;
};

// Create context for theme toggle
export const ThemeContext = React.createContext({ toggleColorMode: () => {}, mode: 'light' });
console.log('App.js loaded');
function App() {
    // Get saved theme preference or default to 'light'
    const [mode, setMode] = useState(() => {
        const savedMode = localStorage.getItem('theme-mode');
        return savedMode || 'light';
    });

    // Toggle between light and dark mode
    const toggleColorMode = () => {
        setMode((prevMode) => {
            const newMode = prevMode === 'light' ? 'dark' : 'light';
            localStorage.setItem('theme-mode', newMode);
            return newMode;
        });
    };

    // Listen for system theme preference changes
    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        
        // Set initial theme based on system preference if no saved preference
        if (!localStorage.getItem('theme-mode')) {
            setMode(mediaQuery.matches ? 'dark' : 'light');
        }
        
        // Update theme when system preference changes
        const handleChange = (e) => {
            if (!localStorage.getItem('theme-mode')) {
                setMode(e.matches ? 'dark' : 'light');
            }
        };
        
        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, []);

    // Initialize analytics
    useEffect(() => {
        analyticsService.initAnalytics({
            // You can override any defaults here
            // enabled: process.env.NODE_ENV === 'production',
        });
    }, []);

    // Log API configuration on startup
    useEffect(() => {
        if (process.env.NODE_ENV === 'development') {
            console.log('API Configuration:', {
                baseUrl: apiConfig.baseUrl,
                environment: process.env.NODE_ENV
            });
        }
    }, []);
    
   

    // Create theme with Islamic colors
    const theme = useMemo(() => createTheme({
        palette: {
            mode,
            primary: {
                main: '#006400',  // Dark Green
                light: '#2e7d32',
                dark: '#004d00',
                contrastText: '#fff'
            },
            secondary: {
                main: '#d4af37',  // Gold
                light: '#e4c04d',
                dark: '#aa8c2c',
                contrastText: '#000'
            },
            background: {
                default: mode === 'light' ? '#f8f7f2' : '#121212',  // Light parchment or dark background
                paper: mode === 'light' ? '#fff' : '#1E1E1E'
            },
            text: {
                primary: mode === 'light' ? '#333333' : '#E0E0E0',
                secondary: mode === 'light' ? '#555555' : '#AAAAAA'
            }
        },
        typography: {
            fontFamily: '"Roboto", "Arial", sans-serif',
            h1: {
                fontWeight: 600
            },
            h2: {
                fontWeight: 600
            },
            h3: {
                fontWeight: 500
            },
            h4: {
                fontWeight: 500
            },
            h5: {
                fontWeight: 500
            },
            h6: {
                fontWeight: 500
            }
        },
        shape: {
            borderRadius: 8
        },
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                  body: {
                    scrollbarWidth: 'thin',
                    '&::-webkit-scrollbar': {
                      width: '8px',
                    },
                    '&::-webkit-scrollbar-track': {
                      background: mode === 'light' ? '#f1f1f1' : '#333',
                    },
                    '&::-webkit-scrollbar-thumb': {
                      background: mode === 'light' ? '#bdbdbd' : '#555',
                      borderRadius: '4px',
                    },
                  },
                },
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        boxShadow: '0 3px 10px rgba(0,0,0,0.08)',
                        transition: 'transform 0.2s',
                        '&:hover': {
                            transform: 'translateY(-3px)'
                        }
                    }
                }
            },
            MuiButton: {
                styleOverrides: {
                    root: {
                        borderRadius: '6px',
                        textTransform: 'none',
                        fontWeight: 500,
                        padding: '8px 16px'
                    },
                    containedPrimary: {
                        background: 'linear-gradient(135deg, #006400, #004d00)',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #004d00, #003b00)'
                        }
                    }
                }
            }
        }
    }), [mode]);

    const themeContextValue = useMemo(
        () => ({
            toggleColorMode,
            mode,
        }),
        [mode]
    );

    return (
        <ThemeContext.Provider value={themeContextValue}>
            <ThemeProvider theme={theme}>
                <CssBaseline />
                <PrayerTimesProvider>
                    <MosqueProvider>
                        <Router>
                            <AthanSettingsProvider>
                                <QuranSettingsProvider>
                                    <Layout>
                                        <ThemeToggle />
                                        <LanguageSelector />
                                        {/* Wrap Routes with Suspense */}
                                        <PageErrorBoundary>
                                        <Suspense fallback={<div style={{ minHeight: '100vh' }} aria-busy="true" />}>
                                            <Routes>
                                                <Route path="/" element={<HomePage />} />
                                                <Route path="/prayer-times" element={<PrayerTimesPage />} />
                                                <Route path="/calendar" element={<CalendarPage />} />
                                                <Route path="/qibla" element={<QiblaPage />} />
                                                <Route path="/mobile-app" element={<MobileAppPage />} />
                                                <Route path="/quran" element={<QuranPage />} />
                                                <Route path="/lifestyle-compass" element={<LifestyleCompass />} />
                                                <Route path="/api" element={<ApiDocPage />} />
                                                <Route path="/privacy-policy" element={<LegacyPrivacyPolicyRedirect />} />
                                                <Route path="/terms" element={<LegacyTermsRedirect />} />
                                                <Route path="/terms-of-service" element={<LegacyTermsRedirect />} />
                                                <Route path="/alexa-skill" element={<AlexaSkillPage />} />
                                                <Route path="/niyyah" element={<NiyyahPage />} />
                                                <Route path="/tasmi" element={<TasmiPage />} />
                                                <Route path="/tasmi/record" element={<TasmiRecordPage />} />
                                                <Route path="/ai-agents" element={<AiAgentsPage />} />
                                                <Route path="/feature-request" element={<FeatureRequestPage />} />
                                                <Route path="/mosque-directory" element={<MosqueDirectoryPage />} />
                                                <Route path="/mosque/:id" element={<MosqueDetailPage />} />
                                                <Route path="/masjid-request" element={<MasjidRequestPage />} />
                                                <Route path="/mihrab" element={<MihrabPage />} />
                                                <Route path="/mihrab/setup" element={<MihrabPage />} />
                                                <Route path="/prayer-intent" element={<PrayerIntentPage />} />
                                                <Route path="/faq-support" element={<FAQSupportPage />} />
                                            </Routes>
                                        </Suspense>
                                        </PageErrorBoundary>
                                    </Layout>
                                </QuranSettingsProvider>
                            </AthanSettingsProvider>
                        </Router>
                    </MosqueProvider>
                </PrayerTimesProvider>
            </ThemeProvider>
        </ThemeContext.Provider>
    );
}

export default App;